import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Avatar } from "@mui/material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import "./npd.css";
import { errorMessage, npdApi } from "./npdApi";
import { enrichParts, startOfToday } from "./npdUtils";
import MainDashboard from "./MainDashboard";
import BuyerDashboard from "./BuyerDashboard";
import DevelopmentTracker from "./DevelopmentTracker";
import AssemblyLines from "./AssemblyLines";
import PartsView from "./PartsView";
import Suppliers from "./Suppliers";
import Reports from "./Reports";
import AddPart from "./AddPart";
import PartDrawer from "./PartDrawer";

export const NPD_ADMIN_BASE = "/npd/admin";
export const NPD_USER_BASE = "/npd/dashboard";

// Sidebar per role: [view, label, hint]. "" is the role's home view.
const ADMIN_NAV = [
  ["", "Main Dashboard", "Overall sourcing overview"],
  ["tracker", "Development Tracker", "Detailed progress by assembly line and part"],
  ["lines", "Assembly Lines", "Assembly-line status and master data"],
  ["parts", "Products / Parts", "View and manage sourcing items"],
  ["suppliers", "Suppliers", "Supplier records"],
  ["reports", "Reports", "Where we are lacking"],
];
const BUYER_NAV = [
  ["", "My Dashboard", "Your parts and next actions"],
  ["add", "Add Part", "Add a new sourcing item"],
  ["tracker", "Development Tracker", "Stage-by-stage progress"],
  ["suppliers", "Suppliers", "Supplier records"],
];
const ADMIN_VIEWS = { "": MainDashboard, tracker: DevelopmentTracker, lines: AssemblyLines, parts: PartsView, suppliers: Suppliers, reports: Reports, add: AddPart };
const BUYER_VIEWS = { "": BuyerDashboard, add: AddPart, tracker: DevelopmentTracker, suppliers: Suppliers };

// New Product Development module. The admin and buyers each land on their own dashboard;
// there is no switching between the two.
const NpdApp = ({ admin }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [search] = useSearchParams();
  const view = (useParams()["*"] || "").split("/")[0];
  const base = admin ? NPD_ADMIN_BASE : NPD_USER_BASE;

  const [ctx, setCtx] = useState(null);
  const [lines, setLines] = useState([]);
  const [rawParts, setRawParts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [drawer, setDrawer] = useState({ open: false, part: null });
  const [busy, setBusy] = useState(false);
  const [toastMsg, setToastMsg] = useState({ text: "", show: false });
  const toastTimer = useRef();
  const mainRef = useRef();
  const today = useMemo(() => startOfToday(), []);

  const toast = useCallback((text) => {
    setToastMsg({ text, show: true });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg((t) => ({ ...t, show: false })), 2800);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await npdApi.context();
        const [l, p, s, b] = await Promise.all([
          npdApi.lines(),
          npdApi.parts(),
          npdApi.suppliers(),
          c.role === "ADMIN" ? npdApi.buyers() : Promise.resolve([]),
        ]);
        if (!alive) return;
        setCtx(c);
        setLines(l);
        setRawParts(p);
        setSuppliers(s);
        setBuyers(b);
      } catch (e) {
        if (alive) setLoadError(errorMessage(e, "Could not load New Product Development."));
      }
    })();
    return () => { alive = false; clearTimeout(toastTimer.current); };
  }, []);

  // Reloads the shared data quietly (no loading screen) so the dashboard shows changes made by
  // other users since the page was opened. Only the latest request is applied.
  const refreshSeq = useRef(0);
  const refreshData = useCallback(async () => {
    const seq = ++refreshSeq.current;
    try {
      const [l, p, s, b] = await Promise.all([
        npdApi.lines(),
        npdApi.parts(),
        npdApi.suppliers(),
        admin ? npdApi.buyers() : Promise.resolve(null),
      ]);
      if (seq !== refreshSeq.current) return;
      setLines(l);
      setRawParts(p);
      setSuppliers(s);
      if (b) setBuyers(b);
    } catch (e) {
      if (seq === refreshSeq.current) toast(errorMessage(e, "Could not refresh the latest data."));
    }
  }, [admin, toast]);

  const parts = useMemo(() => enrichParts(rawParts, lines), [rawParts, lines]);
  const enrichOne = useCallback((p) => enrichParts([p], lines)[0] || null, [lines]);

  const closeDrawer = () => setDrawer((d) => ({ ...d, open: false }));

  const go = useCallback((v, opts = {}) => {
    setDrawer((d) => ({ ...d, open: false }));
    navigate(`${base}${v ? `/${v}` : ""}${opts.line ? `?line=${opts.line}` : ""}`);
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [base, navigate]);

  // Merges a part returned by the API into the list (list rows carry no detail fields)
  const upsertPart = useCallback((p) => {
    const { stageDates, activities, ...summary } = p;
    setRawParts((list) => (list.some((x) => x.id === p.id) ? list.map((x) => (x.id === p.id ? summary : x)) : [...list, summary]));
  }, []);

  const openPart = useCallback(async (id) => {
    const cached = rawParts.find((x) => x.id === id);
    setDrawer({ open: true, part: cached ? enrichOne(cached) : null });
    try {
      setDrawer({ open: true, part: enrichOne(await npdApi.part(id)) });
    } catch (e) {
      toast(errorMessage(e, "Could not open the part."));
    }
  }, [rawParts, enrichOne, toast]);

  const runAction = useCallback(async (id, action, extra = {}) => {
    setBusy(true);
    try {
      const p = await npdApi.action(id, { action, ...extra });
      upsertPart(p);
      setDrawer((d) => (d.part?.id === id ? { ...d, part: enrichOne(p) } : d));
      toast(`${p.partNo}: ${p.activities?.[p.activities.length - 1]?.text || "Updated"}`);
    } catch (e) {
      toast(errorMessage(e, "Could not update the part."));
    } finally {
      setBusy(false);
    }
  }, [upsertPart, enrichOne, toast]);

  const signOut = () => {
    localStorage.clear();
    navigate("/");
  };

  if (loadError) {
    return (
      <div className="npd-root">
        <main>
          <h2>New Product Development</h2>
          <div className="panel empty">{loadError}</div>
          <div className="tools"><button className="btn" onClick={() => navigate("/modules")}>Back to modules</button></div>
        </main>
      </div>
    );
  }
  if (!ctx) {
    return <div className="npd-root"><main><div className="empty">Loading New Product Development...</div></main></div>;
  }

  // Each role only ever sees its own dashboard
  const isAdmin = ctx.role === "ADMIN";
  if (isAdmin !== admin) {
    return <Navigate to={isAdmin ? NPD_ADMIN_BASE : NPD_USER_BASE} replace />;
  }
  const NAV = isAdmin ? ADMIN_NAV : BUYER_NAV;
  const Views = isAdmin ? ADMIN_VIEWS : BUYER_VIEWS;
  if (!(view in Views)) {
    return <Navigate to={base} replace />;
  }
  const View = Views[view];

  const app = {
    isAdmin,
    me: ctx,
    lines,
    parts,
    suppliers,
    buyers,
    today,
    go,
    toast,
    openPart,
    advance: (id) => runAction(id, "ADVANCE"),
    onPartSaved: upsertPart,
    onSupplierAdded: (s) =>
      setSuppliers((list) => (list.some((x) => x.id === s.id) ? list : [...list, s].sort((a, b) => a.name.localeCompare(b.name)))),
    onLineSaved: (l) =>
      setLines((list) => (list.some((x) => x.id === l.id) ? list.map((x) => (x.id === l.id ? l : x)) : [...list, l])),
  };

  return (
    <div className="npd-root">
      <nav>
        {/* Profile section, Modules and Logout follow the Procure (Material Tracker) sidebar */}
        <div className="profile">
          <Avatar sx={{ bgcolor: "var(--acc)", color: "var(--acc-ink)", fontWeight: 600 }}>
            {ctx.name?.split(" ").map((n) => n[0]).join("") || "ND"}
          </Avatar>
          <div>
            <b>{ctx.name || "User"}</b>
            <span>New Product Development</span>
          </div>
        </div>
        {NAV.map(([v, label, hint]) => (
          <button key={v || "home"} className={`tab${view === v ? " on" : ""}`} onClick={() => {
            go(v);
            // The dashboard tab (Main Dashboard / My Dashboard) loads the latest data, also when clicked again
            if (v === "") refreshData();
          }}>
            {label}<small>{hint}</small>
          </button>
        ))}
        <div className="foot">
          <button type="button" className="tab ic" onClick={() => navigate("/modules")}>
            <AppsOutlinedIcon fontSize="small" />
            <span>Modules</span>
          </button>
          <button type="button" className="tab ic" onClick={signOut}>
            <LogoutOutlinedIcon fontSize="small" />
            <span>Logout</span>
          </button>
        </div>
      </nav>
      <main ref={mainRef}>
        <View key={pathname} app={app} lineId={search.get("line")} />
      </main>
      <PartDrawer part={drawer.part} open={drawer.open} app={app} busy={busy}
        onAction={(action, extra) => runAction(drawer.part.id, action, extra)} onClose={closeDrawer} />
      <div className={`toast${toastMsg.show ? " show" : ""}`}>{toastMsg.text}</div>
    </div>
  );
};

export default NpdApp;
